import MessagingServiceManager from './manager';
import type { MessagePayload, UserPresenceObj } from './types';

class UserPresence {
  private users: { [userId: string]: UserPresenceObj };
  private readonly connected: boolean;
  private connectionsCount: number;

  constructor() {
    /*
     * Format:
     * {
     *  "<userid>": {
     *       "state": "<online>",
     *       "subscribers": {
     *           "<subId>": {
     *               callback: <fn>,
     *               contextName: <contextName>
     *           }
     *       }
     *  }
     * }
     */
    this.users = {};
    this.connected = false;
    this.connectionsCount = 1;
  }

  _handleUserPresence({ message }: MessagePayload) {
    const { state, updateById } = message;
    const user = this.users[updateById];
    if (user) {
      user.state = state;

      Object.keys(user.subscribers).forEach((subId) => {
        user.subscribers[subId]?.callback({ state: user.state });
      });
    }
  }

  _getAutoGenId() {
    this.connectionsCount += 1;
    return `${Date.now()}_${this.connectionsCount}`;
  }

  subscribe(userId: string, callback: Function, contextName: string): string {
    const subId = this._getAutoGenId();
    const userObject = this.users[userId];
    if (userObject) {
      userObject.subscribers[subId] = {
        callback,
        contextName
      };
      return subId;
    }

    this.users[userId] = {
      subscribers: {
        [subId]: {
          callback,
          contextName
        }
      }
    };

    const message = {
      matcher: 'PRESENCE',
      criteria: {
        userId
      }
    };

    MessagingServiceManager.subscribe(message, this._handleUserPresence.bind(this));

    return subId;
  }

  unsubscribe(userId: string, subscriptionId: string) {
    const message = {
      matcher: 'PRESENCE',
      unsubscribeUsers: {
        users: [userId]
      }
    };

    if (this.users[userId] && this.users[userId].subscribers) {
      delete this.users[userId].subscribers[subscriptionId];

      if (Object.keys(this.users[userId].subscribers).length === 0) {
        delete this.users[userId];
        MessagingServiceManager.publishMessage(message);
      }
    }
  }

  getUserState(userId: string) {
    return this.users[userId] && this.users[userId].state;
  }

  clearContext(contextName: string) {
    Object.keys(this.users).forEach((userId) => {
      Object.keys(this.users[userId].subscribers).forEach((subId) => {
        const sub = this.users[userId].subscribers[subId];
        if (sub.contextName === contextName) {
          this.unsubscribe(userId, subId);
        }
      });
    });
  }
}

export default UserPresence;
